import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Spinner,
  Table,
} from 'react-bootstrap';
import leadAutomationService from '../../services/leadAutomationService';

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  padding: 18,
};

const dashboardShell = {
  background: 'linear-gradient(145deg, #f4f7fb 0%, #eef2ff 100%)',
  border: '1px solid #dbe4f0',
  borderRadius: 18,
  boxShadow: '0 10px 30px rgba(30, 58, 95, 0.07)',
  padding: 20,
  marginBottom: 24,
};

const metricCard = {
  ...card,
  minHeight: 104,
};

const statusLabels = {
  scheduled: 'Agendada',
  sent: 'Enviada',
  handoff: 'Atendimento humano',
  cancelled: 'Cancelada',
  failed: 'Falha',
};

const decisionLabels = {
  rule_reply: 'Regra segura',
  fallback_reply: 'Confirmação padrão',
  human_handoff: 'Encaminhada ao humano',
  flow_reply: 'Fluxo contextual',
  flow_handoff_reply: 'Fluxo concluído/transferido',
};

const reasonLabels = {
  requested_human: 'Solicitou atendimento humano',
  complaint_or_cancellation: 'Reclamação ou cancelamento',
  urgent_or_emergency: 'Urgência ou emergência',
  clinical_or_medication: 'Dúvida clínica ou medicamento',
  custom_handoff_keyword: 'Regra personalizada da clínica',
  automation_reply_limit_reached: 'Limite de respostas atingido',
  unknown_intent: 'Intenção não reconhecida',
  scheduling_qualification_complete:'Qualificação de agendamento concluída',
  scheduling_topic_changed_to_price:'Mudou o assunto para valores',
  scheduling_topic_changed_to_address:'Mudou o assunto para endereço',
  scheduling_declined:'Desistiu do agendamento',
  invalid_conversation_answer:'Resposta não compreendida no fluxo',
  human_replied_or_connection_inactive:'Atendente respondeu ou conexão ficou inativa',
  newer_inbound_message:'Uma mensagem mais recente foi recebida',
};

const intentLabels = {
  greeting: 'Saudação',
  scheduling: 'Agendamento',
  address: 'Localização',
  price: 'Valores',
};

const formatDate = value => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;

  return remainder ? `${minutes}min ${remainder}s` : `${minutes}min`;
};

const Metric = ({ label, value, help }) => (
  <div className="col-sm-6 col-xl-3">
    <div style={metricCard}>
      <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: '#1e3a5f' }}>
        {value}
      </div>
      {help && <div className="text-muted" style={{ fontSize: 11 }}>{help}</div>}
    </div>
  </div>
);

export default function LeadAutomationDashboard({
  initialPipelineId = '',
}) {  const [filters, setFilters] = useState({
    days: 30,
    pipeline_id: initialPipelineId
      ? String(initialPipelineId)
      : '',
    channel: '',
    status: '',
  });
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setNotice(null);

    try {
      const data = await leadAutomationService.dashboard(filters);
      setDashboard(data);
    } catch (error) {
      setNotice({
        type: 'danger',
        text: error.response?.data?.message
          || 'Não foi possível carregar o monitoramento.',
      });
    } finally {
      setLoading(false);
    }
   }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (name, value) => {
    setFilters(current => ({ ...current, [name]: value }));
  };

  const summary = dashboard?.summary || {};
  const pipelines = dashboard?.pipelines || [];
  const runs = dashboard?.runs || [];
  const intentions = dashboard?.intentions || [];
  const handoffReasons = dashboard?.handoff_reasons || [];
  
  return (
    <div style={dashboardShell}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">Monitoramento do atendimento</h5>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Acompanhe a automação por pipeline, canal e período.
          </div>
        </div>

        <Button variant="outline-primary" onClick={load} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar painel'}
        </Button>
      </div>

      {notice && <Alert variant={notice.type}>{notice.text}</Alert>}

      <div style={{ ...card, marginBottom: 16 }}>
        <div className="row g-3">
          <Form.Group className="col-md-3" controlId="automation-dashboard-period">
            <Form.Label>Período</Form.Label>
            <Form.Select
              value={filters.days}
              onChange={event => setFilter('days', Number(event.target.value))}
            >
              <option value={7}>Últimos 7 dias</option>
              <option value={30}>Últimos 30 dias</option>
              <option value={90}>Últimos 90 dias</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-md-3" controlId="automation-dashboard-pipeline">
            <Form.Label>Pipeline</Form.Label>
            <Form.Select
              value={filters.pipeline_id}
              onChange={event => setFilter('pipeline_id', event.target.value)}
            >
              <option value="">Todos os pipelines</option>
              {pipelines.map(pipeline => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.nome}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-md-3" controlId="automation-dashboard-channel">
            <Form.Label>Canal</Form.Label>
            <Form.Select
              value={filters.channel}
              onChange={event => setFilter('channel', event.target.value)}
            >
              <option value="">Todos os canais</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="instagram">Instagram</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-md-3" controlId="automation-dashboard-status">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={event => setFilter('status', event.target.value)}
            >
              <option value="">Todos os status</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      </div>

      {loading && !dashboard ? (
        <div className="text-center p-5"><Spinner animation="border" /></div>
      ) : (
        <>
          <div className="row g-3 mb-3">
            <Metric label="Mensagens avaliadas" value={summary.total || 0} />
            <Metric label="Respostas enviadas" value={summary.sent || 0} />
            <Metric label="Encaminhadas ao humano" value={summary.handoff || 0} />
            <Metric
              label="Taxa de automação"
              value={`${summary.automation_rate || 0}%`}
            />
            <Metric label="Canceladas" value={summary.cancelled || 0} />
            <Metric label="Falhas" value={summary.failed || 0} />
            <Metric label="Aguardando envio" value={summary.scheduled || 0} />
            <Metric
              label="Tempo médio de resposta"
              value={formatDuration(summary.average_response_seconds)}
              help="Do recebimento até o envio"
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-4">
              <div style={{ ...card, height: '100%' }}>
                <h6>Intenções mais frequentes</h6>

                {intentions.length ? intentions.map(item => (
                  <div
                    key={item.intent}
                    className="d-flex justify-content-between py-2 border-bottom"
                  >
                    <span>
                      {intentLabels[item.intent] || item.intent}
                    </span>

                    <strong>{item.total}</strong>
                  </div>
                )) : (
                  <div className="text-muted">
                    Nenhuma intenção no período.
                  </div>
                )}

                <hr />

                <h6>Motivos de transferência</h6>

                {handoffReasons.length ? handoffReasons.map(item => (
                  <div
                    key={item.reason}
                    className="d-flex justify-content-between py-2 border-bottom"
                  >
                    <span>
                      {reasonLabels[item.reason] || item.reason}
                    </span>

                    <strong>{item.total}</strong>
                  </div>
                )) : (
                  <div className="text-muted">
                    Nenhuma transferência no período.
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-8">
              <div style={card}>
                <h6>Atendimentos recentes</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Lead</th>
                        <th>Pipeline</th>
                        <th>Canal</th>
                        <th>Intenção</th>
                        <th>Decisão</th>
                        <th>Status</th>
                        <th>Resposta / motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map(run => (
                        <tr key={run.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {formatDate(run.created_at)}
                          </td>
                          <td>#{run.lead_id}</td>
                          <td>{run.pipeline?.nome || 'Sem pipeline'}</td>
                          <td>{run.channel === 'whatsapp' ? 'WhatsApp' : 'Instagram'}</td>
                          <td>{intentLabels[run.intent] || run.intent || 'Não reconhecida'}</td>
                          <td>{decisionLabels[run.decision] || run.decision}</td>
                          <td>{statusLabels[run.status] || run.status}</td>
                          <td
                            title={
                              run.reply_text
                                || reasonLabels[run.reason]
                                || run.reason
                                || ''
                            }
                            style={{ maxWidth: 220 }}
                          >
                            <div className="text-truncate">
                              {run.reply_text
                                || reasonLabels[run.reason]
                                || run.reason
                                || '—'}
                              
                            </div>
                          </td>
                        </tr>
                      ))}

                      {!runs.length && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            Nenhum atendimento encontrado para os filtros selecionados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
